/**
 * Date-only formatting (TO1b.2, owner-found 2026-09-10).
 *
 * Postgres DATE columns arrive as plain 'YYYY-MM-DD' strings (the type parser
 * for OID 1082 in backend/config/database.js returns them untouched). Feeding
 * one to `new Date()` parses it as UTC MIDNIGHT, and every render method then
 * shows it in the viewer's zone — so anywhere west of UTC the date goes
 * BACKWARDS by a day: 2026-09-14 renders as "Sep 13" in America/New_York.
 * That is what made an approved Mon–Fri block read as Sun–Thu on the
 * Attendance page and the dashboard widgets.
 *
 * These helpers work on the PARTS and never construct a Date from the string,
 * so the output is identical in every timezone.
 *
 * Use ONLY for date-only values (start_date, end_date, injury_date,
 * expected_return_date, actual_return_date). Real instants — created_at,
 * reviewed_at, timestamps generally — are points in time and SHOULD be shown
 * in the viewer's zone; leave those on the normal Date path.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parts = (value) => {
  if (!value) return null;
  // Accept a bare 'YYYY-MM-DD' or the leading date of an ISO timestamp.
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
};

/** '2026-09-14' -> 'Sep 14, 2026'. Returns '' for null/blank/unparseable. */
export const formatDateOnly = (value) => {
  const p = parts(value);
  if (!p || p.m < 1 || p.m > 12) return '';
  return `${MONTHS[p.m - 1]} ${p.d}, ${p.y}`;
};

/** '2026-09-14' -> 'Sep 14' (no year), for compact rows. */
export const formatDateOnlyShort = (value) => {
  const p = parts(value);
  if (!p || p.m < 1 || p.m > 12) return '';
  return `${MONTHS[p.m - 1]} ${p.d}`;
};

/**
 * Today's CIVIL date in a named zone as 'YYYY-MM-DD'. Uses en-CA, whose short
 * format is already ISO-ordered, so no reassembly is needed.
 */
export const todayInZone = (timeZone = 'America/New_York') =>
  new Date().toLocaleDateString('en-CA', { timeZone });

export default formatDateOnly;

/** The civil date `days` ahead in a named zone, as 'YYYY-MM-DD'. */
export const todayInZonePlus = (days, timeZone = 'America/New_York') =>
  new Date(Date.now() + days * 86400000).toLocaleDateString('en-CA', { timeZone });

/** Convenience for the Attendance history filter's default 30-day window. */
export const todayInZone30Ahead = () => todayInZonePlus(30);

/* ------------------------------------------------------------------
 * TO1c month maths (R-TO7). The Who's Out calendar walks a month grid,
 * and the TO1b.2 bug class is exactly what a naive implementation would
 * reintroduce: `new Date('2026-09-01')` is UTC midnight, so west of UTC
 * every cell would be labelled a day early. NOTHING below constructs a
 * Date from a 'YYYY-MM-DD' string — the grid is built from integer
 * year/month/day parts with Howard Hinnant's civil algorithm, the same
 * one routes/timeOff.js uses server-side, so a cell's date is identical
 * in every viewer timezone.
 * ------------------------------------------------------------------ */

/** Days since the epoch for a civil y-m-d. Pure integer arithmetic. */
const daysFromCivil = (y, m, d) => {
  y -= m <= 2 ? 1 : 0;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
};

/** 0 = Sunday .. 6 = Saturday for a civil y-m-d (1970-01-01 was a Thursday). */
export const dayOfWeek = (y, m, d) => ((daysFromCivil(y, m, d) + 4) % 7 + 7) % 7;

/** Days in a civil month, leap years included. */
export const daysInMonth = (y, m) =>
  m === 2 ? (((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 29 : 28)
          : ([4, 6, 9, 11].includes(m) ? 30 : 31);

/** (2026, 9, 14) -> '2026-09-14'. */
export const isoDate = (y, m, d) =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** Shift a {y, m} cursor by whole months. */
export const addMonths = (y, m, delta) => {
  const t = y * 12 + (m - 1) + delta;
  return { y: Math.floor(t / 12), m: (t % 12 + 12) % 12 + 1 };
};

/** '2026-09-14' -> { y, m, d }, or null. Exported for the month cursor. */
export const dateParts = (value) => parts(value);

/**
 * Inclusive range test on date-only strings. Same-shape ISO dates compare
 * lexicographically exactly as they compare chronologically, so this needs
 * no Date object and no timezone.
 */
export const withinRange = (date, start, end) =>
  !!date && !!start && !!end && date >= start && date <= end;

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'];
